/*
 * @providesModule StayInLoopView
 */

import React, {useState, useEffect} from 'react';
import {Text, View, TouchableOpacity, BackHandler, Platform} from 'react-native';
import {_m} from 'INTL';
import styles from 'StayInLoopStyle';
import SkillzSVG from 'SkillzSVG';
import Sizes from 'Sizes';
import {GenericHeader, BannerAnimated, SkillzTextInput} from '@skillz/ui';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import source_svgs from '../../source_svgs/_source_svgs';
import SkillzImage from 'SkillzImage';
import Toggle from 'react-native-toggle-element';

const ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT = 409;

interface StayInLoopProps {
  //Boolean to show/hide social sign up options
  showGoogleLogin?: boolean;
  showFacebookLogin?: boolean;
  showAppleLogin?: boolean;
  //Callback on “Close” button
  onClosePress: () => void;
  //Handle callback on "Next" button
  handleNextPress: (username: string, isConsent: boolean) => Promise<string>;
  //Handle callback on "Continue with Apple/facebook/google" button
  onContinueWithIDPPress: (fedType: string) => Promise<number>;
  //Handle callback for next press without consent
  handleNextPressWithoutConsent?: () => void;
  //Prevent the close button from appearing
  hideCloseButton?: boolean;
}

export const TEST_IDS = {
  modalTestID: 'sign_up_modal',
  titleTestID: 'sign_up_title',
  secondHeader: 'sign_up_header',
  firstDepositTextTestID: 'sign_up_first_deposit_message',
  messageTestID: 'sign_up_message',
  inputUsernameTestID: 'sign_up_username_input',
  inputPasswordTestID: 'sign_up_password_input',
  signUpTestID: 'sign_up_button',
  haveAccountTestID: 'sign_up_have_account_message',
  signInTestID: 'sign_up_have_account_sign_in_button',
  closeTestID: 'sign_up_close',
  googleTestID: 'sign_up_google_sign_up_button',
  facebookTestID: 'sign_up_facebook_sign_up_button',
  appleTestID: 'sign_up_apple_sign_up_button',
  rewardsMessageTestID: 'sign_up_rewards_message',
  ribbonMessageTestID: 'sign_up_progress_message',
  customerSupportMessageTestID: 'sign_up_support_message',
  stayInLoopHeaderTestID: 'age_verification_stay_in_loop_header',
  emailConsentToggleTestID: 'age_verification_email_consent_toggle',
  stayInLoopMessageTestID: 'age_verification_stay_in_loop_message',
};

type SocailLoginProps = {
  social: string;
  buttonLabel: string;
  testId: string;
  image: string;
  handleSocialEmailLinking: (fedType: string) => void;
};

const SocialLogin = ({
  social,
  buttonLabel,
  testId,
  image,
  handleSocialEmailLinking,
}: SocailLoginProps) => {
  return (
    <TouchableOpacity
      style={styles.fedLoginBtn}
      onPress={() => handleSocialEmailLinking(social)}
      testID={testId}
    >
      <SkillzSVG source={image} style={styles.socialIcons} />
      <Text style={styles.fedLoginText}>{buttonLabel}</Text>
    </TouchableOpacity>
  );
};

const StayInLoopView = (props: StayInLoopProps) => {
  // //Error Banner Visible
  const [errorBannerVisible, setErrorBannerVisible] = useState<boolean>(false);
  //Server Error Banner Visible
  const [serverErrorVisible, setServerErrorVisible] = useState<boolean>(false);
  //Boolean for if federated login options should be shown
  const showGoogleLogin = props.showGoogleLogin ?? false;
  const showFacebookLogin = props.showFacebookLogin ?? false;
  const showAppleLogin = props.showAppleLogin ?? false;
  const [inputEmailError, setInputEmailError] = useState<boolean>(false);
  const [signUpDisabled, setSignUpDisabled] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [isConsent, setIsConsent] = useState(true);
  const toggleSwitch = () => setIsConsent((previousState) => !previousState);

  const extraScrollHeight = 120;

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    BackHandler.addEventListener('hardwareBackPress', () => {
      if (props.hideCloseButton) {
        return true;
      }
      props.onClosePress();
      return true;
    });
    return () => {
      BackHandler.removeEventListener('hardwareBackPress', () => undefined);
    };
  }, []);

  const emailInputCheck = (username: string) => {
    const reg = /^\w+([+]?[\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w\w+)+$/;
    if (!reg.test(username)) {
      setInputEmailError(true);
      return false;
    } else {
      setInputEmailError(false);
      return true;
    }
  };

  const handleNextPress = async () => {
    //need to check if "stay in loop" is enabled, if not just go to play tab
    //if enabled, update CurrentUserStore
    if (!isConsent) {
      props.handleNextPressWithoutConsent?.();
      return;
    }
    // Consent is on: validate email before proceeding
    const checkEmail = emailInputCheck(email);
    if (!checkEmail || signUpDisabled) {
      setInputEmailError(true);
      return;
    }
    try {
      setSignUpDisabled(true);
      setServerErrorVisible(false);
      setErrorBannerVisible(false);
      const error = await props.handleNextPress(email, isConsent);
      if (error) {
        setSignUpDisabled(false);
        if (error === 'SIGN_UP_EMAIL_EXISTING_ACCOUNT' || error === 'EMAIL_ALREADY_EXISTS') {
          setErrorBannerVisible(true);
        } else {
          setServerErrorVisible(true);
        }
      }
    } catch (error) {
      setSignUpDisabled(false);

      if (error === 'Another account is already using this email address') {
        setErrorBannerVisible(true);
      } else {
        setServerErrorVisible(true);
      }
    }
  };

  const handleSocialEmailLinking = async (fedType: string) => {
    try {
      const error = await props.onContinueWithIDPPress(fedType);
      if (error === ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT) {
        setErrorBannerVisible(true);
      } else if (error) {
        setServerErrorVisible(true);
      }
    } catch (error) {
      console.warn('Error in federated login press', error);
    }
  };

  const renderErrorBanner = () => {
    if (errorBannerVisible) {
      return (
        <BannerAnimated
          title={_m('Account already exists')}
          message={_m('This email address already exists.')}
          onClosePress={() => setErrorBannerVisible(false)}
        />
      );
    }
    if (serverErrorVisible) {
      return (
        <BannerAnimated
          title={_m('Server error')}
          message={_m('Sorry, there’s an issue with our sign up service. Please try again later.')}
          onClosePress={() => setServerErrorVisible(false)}
        />
      );
    }
    return false;
  };

  const getConsentTopMargin = () => {
    if (Sizes.isTablet()) {
      return 59;
    }
    return 54;
  };

  const renderEmailConsent = () => {
    return (
      <View style={[styles.textContainer, {marginTop: getConsentTopMargin()}]}>
        <View style={styles.rowContainer}>
          <Text style={styles.headerText} testID={TEST_IDS.stayInLoopHeaderTestID}>
            {_m('Stay in the loop?')}
          </Text>
          <View testID={isConsent ? TEST_IDS.emailConsentToggleTestID : ''}>
            <Toggle
              value={isConsent}
              onPress={toggleSwitch}
              thumbActiveComponent={
                <View testID={TEST_IDS.emailConsentToggleTestID}>
                  <SkillzSVG source={'check_mark.svg'} style={styles.checkIcon} />
                </View>
              }
              trackBarStyle={{
                borderColor: isConsent ? '#4890E8' : '#999999',
              }}
              trackBar={{
                activeBackgroundColor: '#4890E8',
                inActiveBackgroundColor: '#999999',
                width: Sizes.isTablet() ? 57 : 48,
                height: Sizes.isTablet() ? 31 : 26,
                borderWidth: 2,
              }}
              thumbButton={{
                width: Sizes.isTablet() ? 30 : 25,
                height: Sizes.isTablet() ? 30 : 25,
                activeBackgroundColor: '#4890E8',
                inActiveBackgroundColor: '#FFFFFF',
              }}
            />
          </View>
        </View>
        <Text style={styles.descriptionText} testID={TEST_IDS.stayInLoopMessageTestID}>
          {_m('Skillz can reach out to me about promotions, offers, and more.')}
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.container} testID={TEST_IDS.modalTestID}>
      <View style={styles.headerContainer}>
        <GenericHeader
          title={_m('Stay in the Loop')}
          rightIcon={props.hideCloseButton ? undefined : source_svgs['close.svg']}
          onRightPress={() => {
            props.onClosePress();
          }}
          containerStyle={{justifyContent: 'center'}}
        />
      </View>
      <View>
        <KeyboardAwareScrollView
          style={styles.keyboardAvoidContainer}
          enableOnAndroid={true}
          extraScrollHeight={extraScrollHeight}
        >
          {renderErrorBanner()}
          <View>
            <SkillzImage
              style={styles.background}
              source={
                Sizes.isTablet()
                  ? 'stay_in_loop_background_tablet.png'
                  : 'stay_in_loop_background.png'
              }
            />
            <View style={styles.saveAccountContainer}>
              <View style={styles.saveAccountHeadersContainer}>
                <Text style={styles.subBodyText} testID={TEST_IDS.rewardsMessageTestID}>
                  {_m('Receive exclusive offers, prizes, and products when you connect')}
                </Text>
              </View>
            </View>
          </View>
          {showAppleLogin || showFacebookLogin || showGoogleLogin ? (
            <View style={styles.socialLoginContainer}>
              <View style={styles.fedLoginBtnContainer}>
                {showFacebookLogin ? (
                  <SocialLogin
                    social={'facebook'}
                    buttonLabel={_m('Continue with Facebook')}
                    testId={TEST_IDS.facebookTestID}
                    image={'facebook.svg'}
                    handleSocialEmailLinking={handleSocialEmailLinking}
                  />
                ) : null}
                {showAppleLogin ? (
                  <SocialLogin
                    social={'apple'}
                    buttonLabel={_m('Continue with Apple')}
                    testId={TEST_IDS.appleTestID}
                    image={'apple.svg'}
                    handleSocialEmailLinking={handleSocialEmailLinking}
                  />
                ) : null}
                {showGoogleLogin ? (
                  <SocialLogin
                    social={'google'}
                    buttonLabel={_m('Continue with Google')}
                    testId={TEST_IDS.googleTestID}
                    image={'google.svg'}
                    handleSocialEmailLinking={handleSocialEmailLinking}
                  />
                ) : null}
              </View>
              <View style={styles.orContainer}>
                <View style={[styles.fedDivider, {marginStart: 8}]} />
                <Text style={styles.orText}>{_m('OR')}</Text>
                <View style={[styles.fedDivider, {marginEnd: 8}]} />
              </View>
            </View>
          ) : null}
          {showAppleLogin || showFacebookLogin || showGoogleLogin ? (
            <View style={styles.skillzSignUpTextContainer}>
              <Text style={styles.skillzSignUpText} testID={TEST_IDS.secondHeader}>
                {_m('Continue with Email')}
              </Text>
            </View>
          ) : null}
          <View style={styles.inputView}>
            <SkillzTextInput
              autoCorrect={false}
              focusColor={inputEmailError ? '#DD6952' : '#4890E8'}
              autoCapitalize={'none'}
              testID={TEST_IDS.inputUsernameTestID}
              label={_m('Email address')}
              value={email}
              onChangeText={(value) => setEmail(value)}
              inputStyle={styles.textInInput}
              keyboardType={'email-address'}
            />
          </View>
          {inputEmailError ? (
            <View style={styles.errorContainer}>
              <SkillzSVG style={styles.errorIcon} source={'info_error.svg'} />
              <Text style={styles.errorMessage}>{_m('Invalid email address')}</Text>
            </View>
          ) : null}
          {renderEmailConsent()}
          <TouchableOpacity
            onPress={handleNextPress}
            testID={TEST_IDS.signUpTestID}
            style={styles.buttonContainer}
          >
            <Text style={styles.loginText}>{_m('Next')}</Text>
          </TouchableOpacity>
        </KeyboardAwareScrollView>
      </View>
    </View>
  );
};

export default StayInLoopView;
